import { useState } from 'react';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import FadeIn from '@/shared-components/animations/components/FadeIn';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import SuccessText from '@/shared-components/text/components/SuccessText';
import Text from '@/shared-components/text/components/Text';

import ForgotPassword from './ForgotPassword';

import './styles/ChangePasswordForm.css';

export default function ChangePasswordForm() {
  const { state } = useStateHandler();
  const t = useTranslations('common.user_settings.login.change_password');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  async function changePassword(e) {
    e.preventDefault();
    setLoading(true);
    setError(null); 
    setSuccess(false);

    if(e.target.user_new_password.value !== e.target.user_confirm_new_password.value) {
      setLoading(false);
      setError({ element: "user_confirm_new_password", error_code: "PASSWORD_MISMATCH" });
      return;
    }

    const newPasswordData = {
      user_old_password: e.target.user_old_password.value,
      user_new_password: e.target.user_new_password.value
    };

    const { response, data } = await UserService.changePassword(newPasswordData);

    if(response.ok) {
      setLoading(false);
      setSuccess(true);
    } else if(response.status === 400) {
      setLoading(false);
      setError({ element: data.error_data.element, error_code: data.error_type });
    }
  }

  return (
    <>
      <FadeIn>
        <form onSubmit={changePassword} className="settings-change-password__form">
          <Text className="settings-change-password__heading font-size-medium">
            {t('heading')}
          </Text>

          <PublicFormInput 
            error={error} 
            placeholder={t('placeholders.old_password')}
            type="password" 
            element="user_old_password" 
            readOnly={loading || success}
            margin 
          />

          <PublicFormInput 
            error={error} 
            placeholder={t('placeholders.new_password')}
            type="password" 
            element="user_new_password" 
            readOnly={loading || success}
            margin 
          />

          <PublicFormInput 
            error={error} 
            placeholder={t('placeholders.confirm_new_password')}
            type="password" 
            element="user_confirm_new_password" 
            readOnly={loading || success}
          />

          <ForgotPassword userEmail={state.user_session.user_email}/>

          <ContinueButton 
            loading={loading} 
            success={success} 
            successText={t('buttons.password_updated')}
          >
            {t('buttons.change_password')}
          </ContinueButton>
        </form>

        {success && (
          <SuccessText className="settings-change-password__success-text">
            {t('success_message')}
          </SuccessText>
        )}
      </FadeIn>
    </>
  );
}
